import React, { useState, useEffect, useMemo } from "react";
import {
    LottieLoadingIndicator,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalSection,
    StandardModalSectionTitle,
    StandardModalState,
    UserSelectionMetricCopy,
    UserSelectionMetricDescription,
    UserSelectionMetricSummary,
    UserSelectionMetricTitle,
    UserSelectionMetricValue,
    UserViewerListPreview
} from "src/components";
import axios from "axios";
import { API_URL } from "src/scenes/App";
import { userTypeTitleFromReaderTypes } from "../utils";
import { FILTER_NOTICE } from "./../Noticeboard";
import pluralize from "pluralize";
import { capitalizeString, customNames } from "src/utils/helpers";

const NoticeViewershipModal = ({ noticeData, showNoticeViewershipModal, handleCloseNoticeViewershipModal, user }) => {

    const [viewersList, setViewersList] = useState([]);
    const [isFetching, setIsFetching] = useState(false);

    const publishedToEmployees = noticeData.publications.some(publication => publication.readerType === FILTER_NOTICE.EMPLOYEE);
    const publishedToClients = noticeData.publications.some(publication => publication.readerType === FILTER_NOTICE.CLIENT);
    const viewsCountFormatted = parseInt(noticeData.readCount).toLocaleString("en-us");
    const userTypeTitle = userTypeTitleFromReaderTypes(noticeData);

    useEffect(() => {
        fetchViewerList();
    }, [user.token]);
    
    const fetchViewerList = async () => {
        const noticeboardApi = `${API_URL}/company/noticeboard/viewersList/${noticeData.uuid}`;
        
        setIsFetching(true);
        try {
            const res = await axios.get(noticeboardApi, {
                headers: {
                    Authorization: "Bearer " + user.token
                }
            });
            
            setViewersList(res.data.viewerDetails);
        } catch {
            // Handle error
        } finally {
            setIsFetching(false);
        }
    };

    const employeeViewers = useMemo(() => (
        viewersList.filter(viewer => !viewer.isClient)
    ), [viewersList]);

    const clientViewers = useMemo(() => (
        viewersList.filter(viewer => viewer.isClient)
    ), [viewersList]);

    return (
        <StandardModal
            open={showNoticeViewershipModal}
            onClose={handleCloseNoticeViewershipModal}
            title={`Post ${userTypeTitle} Views`}
            widthMode="wide"
            footer={(
                <StandardModalActionGroup $pushRight>
                    <StandardModalActionButton type="button" onClick={handleCloseNoticeViewershipModal}>
                        Close
                    </StandardModalActionButton>
                </StandardModalActionGroup>
            )}
        >
            <StandardModalSection>
                <UserSelectionMetricSummary>
                    <UserSelectionMetricCopy>
                        <UserSelectionMetricTitle>Number of {userTypeTitle} Views</UserSelectionMetricTitle>
                        <UserSelectionMetricDescription>
                            This is the number of {userTypeTitle.toLowerCase()}s who have seen the selected post on your business Noticeboard.
                        </UserSelectionMetricDescription>
                    </UserSelectionMetricCopy>
                    <UserSelectionMetricValue>{viewsCountFormatted}</UserSelectionMetricValue>
                </UserSelectionMetricSummary>
            </StandardModalSection>

            <StandardModalSection>
                {isFetching ? (
                    <StandardModalState minHeight="20rem">
                        <LottieLoadingIndicator />
                    </StandardModalState>
                ) : (
                    <>
                        {publishedToEmployees && (
                            <>
                                <StandardModalSectionTitle>{capitalizeString(pluralize(customNames.employees))}</StandardModalSectionTitle>
                                <UserViewerListPreview
                                    viewersList={employeeViewers}
                                    isReaderTypeEmployee={true}
                                    emptyMessage="No employee views found"
                                />
                            </>
                        )}
                        {publishedToClients && (
                            <>
                                <StandardModalSectionTitle>{capitalizeString(pluralize(customNames.clients))}</StandardModalSectionTitle>
                                <UserViewerListPreview
                                    viewersList={clientViewers}
                                    isReaderTypeEmployee={false}
                                    clientSecondaryHeader="Job Title"
                                    emptyMessage="No client views found"
                                />
                            </>
                        )}
                    </>
                )}
            </StandardModalSection>
        </StandardModal>
    );
};

export default NoticeViewershipModal;
